import { TrashIcon } from "@heroicons/react/24/outline";
import AssistantMessage from "./AssistantMessage";

function formatConversationDate(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AssistantWorkspace({
  messages,
  conversations,
  activeConversationId,
  input,
  setInput,
  loading,
  historyLoading,
  canSend,
  sendMessage,
  handleKeyDown,
  listEndRef,
  inputRef,
  openConversation,
  startNewConversation,
  askDeleteConversation,
}) {
  const historyDisabled = loading || historyLoading;
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
      <div className="card-custom overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3">
          <button
            onClick={startNewConversation}
            disabled={loading}
            className="w-full rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            New Conversation
          </button>
        </div>

        <div className="max-h-[620px] overflow-y-auto px-2 py-2">
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            History
          </p>

          {historyLoading && (
            <div className="px-2 py-2 text-sm text-gray-500">Loading history…</div>
          )}

          {!historyLoading && conversations.length === 0 && (
            <div className="px-2 py-2 text-sm text-gray-500">
              No previous conversations yet.
            </div>
          )}

          <div className="space-y-1">
            {conversations.map((conversation) => {
              const conversationId = conversation.id || conversation.uuid;
              const isActive = conversationId === activeConversationId;

              return (
                <div
                  key={conversationId}
                  className={`flex items-start gap-1 rounded-lg px-1 py-1 transition ${
                    isActive ? "bg-indigo-50" : "hover:bg-gray-50"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => openConversation(conversationId)}
                    disabled={historyDisabled}
                    className={`min-w-0 flex-1 rounded-md px-2 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                      isActive ? "text-indigo-700" : "text-gray-700"
                    }`}
                  >
                    <div className="line-clamp-2 font-medium">
                      {conversation.title || "New Conversation"}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {formatConversationDate(conversation.last_activity_at)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      askDeleteConversation(conversation);
                    }}
                    disabled={historyDisabled}
                    aria-label="Delete conversation"
                    className="shrink-0 rounded-md p-2 text-gray-400 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card-custom">
        <div className="h-[560px] overflow-y-auto px-4 py-4 space-y-4">
          {!loading && messages.length === 0 && (
            <div className="text-sm text-gray-500">
              Ask a question to get started.
            </div>
          )}

          {messages.map((message, index) => (
            <AssistantMessage
              key={message.id || `message-${index}`}
              message={message}
            />
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl border border-gray-200 bg-slate-50 px-4 py-3 text-sm text-gray-500">
                Preparing answer…
              </div>
            </div>
          )}

          <div ref={listEndRef} />
        </div>

        <div className="border-t border-gray-200 px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              placeholder="Ask about a lot, receiving, shipment, product, barcode, or customer…"
              className="min-h-[42px] flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={!canSend}
              className="min-h-[42px] rounded-md bg-main-purple px-5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
